/**
 * ProposalCashflowTable — Year-by-year cashflow table
 *
 * Displays savings, costs, net savings, and cumulative totals per year.
 * Supports both ESA and CapEx modes.
 * Shows first 5 years by default with expand toggle.
 * Highlights the payback year (first cumulative > 0) for CapEx mode.
 */

import React, { useState, useMemo } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { vortxColors, vortxTypography, calculateCashflows } from '@vortx/shared-ui';
import { formatCurrency } from './extractProposalData';

function buildEsaRows(data) {
  const { annualCashflows, bauAnnualCost, optimizedUtilityBill } = data;
  const annualSavings = bauAnnualCost - optimizedUtilityBill;

  if (annualCashflows && annualCashflows.length > 0) {
    let cumulative = 0;
    return annualCashflows.map((cf) => {
      const savings = annualSavings;
      const net = cf.netCashflow;
      const costs = savings - net;
      cumulative += net;
      return {
        year: cf.year,
        savings,
        costs,
        net,
        cumulative,
      };
    });
  }

  // Fallback estimate if annualCashflows is missing
  const term = data.esaTerms?.term || 20;
  const escalation = data.esaTerms?.escalation || 0;
  const year1Esa = data.year1EsaPayment || 0;
  const rows = [];
  let cumulative = 0;

  for (let y = 1; y <= term; y++) {
    const costs = year1Esa * Math.pow(1 + escalation, y - 1);
    const net = annualSavings - costs;
    cumulative += net;
    rows.push({
      year: y,
      savings: annualSavings,
      costs,
      net,
      cumulative,
    });
  }

  return rows;
}

function buildCapexRows(data) {
  const term = data.capexTerms?.term || 20;
  const netCost = data.capexTerms?.netCost || 0;
  const savings = data.year1AnnualSavings || 0;
  const om = data.year1OM || 0;
  const rows = [];

  // Year 0: initial investment
  if (netCost > 0) {
    rows.push({
      year: 0,
      savings: 0,
      costs: netCost,
      net: -netCost,
      cumulative: -netCost,
    });
  }

  let cumulative = netCost > 0 ? -netCost : 0;
  for (let y = 1; y <= term; y++) {
    const net = savings - om;
    cumulative += net;
    rows.push({
      year: y,
      savings,
      costs: om,
      net,
      cumulative,
    });
  }

  return rows;
}

/**
 * Build recalculated rows using overridden escalation rates.
 * Uses calculateCashflows() from shared-ui for consistent math.
 */
function buildOverriddenRows(data, rateOverrides) {
  const term = data.mode === 'esa'
    ? (data.esaTerms?.term || 20)
    : (data.capexTerms?.term || 20);

  const yearOneSavings = data.mode === 'esa'
    ? (data.bauAnnualCost - data.optimizedUtilityBill)
    : (data.year1AnnualSavings || 0);

  const yearOneOmCost = data.mode === 'capex' ? (data.year1OM || 0) : 0;

  const cashflows = calculateCashflows({
    analysisYears: term,
    yearOneSavings,
    yearOneOmCost,
    escalationRates: {
      energy: (rateOverrides.energyEscalation || 3) / 100,
      labor: (rateOverrides.laborEscalation || 2.5) / 100,
      naturalGasDelivery: (rateOverrides.gasDeliveryEscalation || 2.5) / 100,
      naturalGasCommodity: (rateOverrides.gasCommodityEscalation || 3) / 100,
    },
    degradationRate: (rateOverrides.degradationRate || 0.5) / 100,
  });

  const rows = [];
  let cumulative = 0;

  // Year 0 for CapEx
  if (data.mode === 'capex' && data.capexTerms?.netCost > 0) {
    cumulative = -(data.capexTerms.netCost);
    rows.push({ year: 0, savings: 0, costs: data.capexTerms.netCost, net: cumulative, cumulative });
  }

  // ESA: payment escalates at contractual rate (not customer-editable)
  const esaYear1Payment = data.year1EsaPayment || 0;
  const esaEscalation = data.esaTerms?.escalation || 0;

  cashflows.forEach((cf) => {
    let savings, costs, net;

    if (data.mode === 'esa') {
      savings = cf.energySavings;
      costs = esaYear1Payment * Math.pow(1 + esaEscalation, cf.year - 1);
      net = savings - costs;
    } else {
      savings = cf.energySavings;
      costs = cf.omCost;
      net = cf.netCashflow;
    }

    cumulative += net;
    rows.push({ year: cf.year, savings, costs, net, cumulative });
  });

  return rows;
}

export default function ProposalCashflowTable({ data, rateOverrides }) {
  const [expanded, setExpanded] = useState(false);

  const rows = useMemo(() => {
    if (!data) return [];

    // If customer has overridden rates, recalculate using shared-ui
    if (rateOverrides) {
      return buildOverriddenRows(data, rateOverrides);
    }

    if (data.mode === 'esa') {
      return buildEsaRows(data);
    }

    if (data.mode === 'capex') {
      return buildCapexRows(data);
    }

    return [];
  }, [data, rateOverrides]);

  if (!rows || rows.length === 0) return null;

  const displayRows = expanded ? rows : rows.slice(0, 5);
  const paybackYear = rows.find((r) => r.cumulative > 0)?.year;

  const headers = ['Year', 'Savings', 'Costs', 'Net', 'Cumulative'];

  return (
    <Box>
      <Typography
        variant="overline"
        sx={{
          color: vortxColors.stone,
          fontWeight: 600,
          letterSpacing: '0.1em',
          mb: 2,
          display: 'block',
        }}
      >
        Year-by-Year Cashflows
      </Typography>

      <Box
        sx={{
          borderRadius: 2,
          border: '1px solid rgba(255,255,255,0.08)',
          bgcolor: 'rgba(255,255,255,0.03)',
          overflow: 'hidden',
        }}
      >
        {/* Table header */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '60px 1fr 1fr 1fr 1fr',
            px: 2,
            py: 1.5,
            borderBottom: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          {headers.map((h) => (
            <Typography
              key={h}
              variant="caption"
              sx={{
                color: vortxColors.stone,
                fontWeight: 600,
                fontSize: '0.7rem',
                textAlign: h === 'Year' ? 'left' : 'right',
              }}
            >
              {h}
            </Typography>
          ))}
        </Box>

        {/* Table rows */}
        {displayRows.map((row, i) => (
          <Box
            key={row.year}
            sx={{
              display: 'grid',
              gridTemplateColumns: '60px 1fr 1fr 1fr 1fr',
              px: 2,
              py: 1,
              borderTop: i > 0 ? '1px solid rgba(255,255,255,0.04)' : 'none',
              bgcolor:
                paybackYear === row.year && data.mode === 'capex'
                  ? 'rgba(191,163,79,0.08)'
                  : 'transparent',
            }}
          >
            <Typography
              sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.8rem',
                color: vortxColors.stone,
              }}
            >
              {row.year}
            </Typography>
            <Typography
              sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.8rem',
                color: vortxColors.white,
                textAlign: 'right',
              }}
            >
              {formatCurrency(row.savings)}
            </Typography>
            <Typography
              sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.8rem',
                color: vortxColors.stone,
                textAlign: 'right',
              }}
            >
              {formatCurrency(row.costs)}
            </Typography>
            <Typography
              sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.8rem',
                color: row.net >= 0 ? vortxColors.gold : '#ef4444',
                textAlign: 'right',
              }}
            >
              {formatCurrency(row.net)}
            </Typography>
            <Typography
              sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.8rem',
                color: row.cumulative >= 0 ? vortxColors.gold : vortxColors.stone,
                textAlign: 'right',
                fontWeight: paybackYear === row.year ? 700 : 400,
              }}
            >
              {formatCurrency(row.cumulative)}
            </Typography>
          </Box>
        ))}
      </Box>

      {rows.length > 5 && (
        <Box sx={{ textAlign: 'center', mt: 1 }}>
          <Button
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{
              color: vortxColors.stone,
              fontSize: '0.75rem',
              textTransform: 'none',
            }}
          >
            {expanded ? 'Show less' : `Show all ${rows.length} years`}
          </Button>
        </Box>
      )}
    </Box>
  );
}
